import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ApiError, auth as authApi } from "@/lib/api";

/**
 * `/verify-email?token=<tok>` — landing page for the verification link
 * Skarp emails after registration. Hits our `/api/auth/verify_email`
 * proxy, which calls Skarp's lambda; we surface the result.
 *
 * UX states:
 *   - **loading**: while the verify call is in flight
 *   - **success**: ✓ message + "Sign in" CTA
 *   - **expired**: token timed out → offer "send a new link"
 *   - **error**: token invalid / unknown / network → offer resend if the
 *     message suggests the token is bad rather than the address
 *
 * Skarp's `/auth/verify-email` is a GET so refreshing this page actually
 * re-attempts the verify (which is fine — the endpoint is idempotent on
 * already-verified tokens).
 */

type VerifyStatus = "loading" | "success" | "expired" | "error";

export function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";

  const [status, setStatus] = useState<VerifyStatus>("loading");
  const [message, setMessage] = useState("");

  // Resend sub-flow
  const [resendBusy, setResendBusy] = useState(false);
  const [resendMessage, setResendMessage] = useState<string | null>(null);

  // React StrictMode double-invokes effects in dev — guard against firing
  // the verify call twice. (In prod this isn't an issue, but the guard is
  // cheap.)
  const calledRef = useRef(false);

  useEffect(() => {
    if (calledRef.current) return;
    calledRef.current = true;
    if (!token) {
      setStatus("error");
      setMessage("This verification link is missing its token. Try clicking the link in your email again.");
      return;
    }
    void authApi
      .verifyEmail(token)
      .then((res) => {
        if (res.ok) {
          setStatus("success");
          setMessage(res.message ?? "Your email has been verified.");
        } else {
          const msg = res.message ?? "We couldn't verify your email.";
          setMessage(msg);
          setStatus(/expired/i.test(msg) ? "expired" : "error");
        }
      })
      .catch((err) => {
        if (err instanceof ApiError) {
          const body = err.body as { message?: string; error?: string } | null;
          const msg = body?.message ?? body?.error ?? `Verification failed (${err.status})`;
          setMessage(msg);
          setStatus(/expired/i.test(msg) ? "expired" : "error");
        } else {
          setStatus("error");
          setMessage("An unexpected error happened. Try again, or request a fresh verification email.");
        }
      });
  }, [token]);

  const onResend = async () => {
    const email = window.prompt(
      "We'll send a fresh verification email. What address did you register with?",
      "",
    );
    if (!email) return;
    setResendBusy(true);
    setResendMessage(null);
    try {
      const r = await authApi.resendVerification(email.trim());
      setResendMessage(r.message);
    } catch {
      // Server always returns ok in normal operation; only network errors
      // surface here.
      setResendMessage("If that email is registered and unverified, a new link is on its way.");
    } finally {
      setResendBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-zinc-950">
      <header className="border-b border-zinc-200 dark:border-zinc-800">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3">
          <Link to="/" className="font-semibold tracking-tight text-lg">
            ← Coauthor
          </Link>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md text-center">
          <StatusIcon status={status} />
          <h1 className="text-2xl font-semibold mt-4 mb-2">
            {titleFor(status)}
          </h1>
          <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-6">
            {message || "Working on it…"}
          </p>

          {status === "success" && (
            <div className="space-y-3">
              <Link
                to="/login"
                className="inline-flex items-center justify-center rounded-md bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-900 text-white px-5 py-2.5 text-sm font-medium hover:opacity-90"
              >
                Sign in to Coauthor
              </Link>
            </div>
          )}

          {status === "expired" && (
            <div className="space-y-3">
              <button
                type="button"
                onClick={onResend}
                disabled={resendBusy}
                className="inline-flex items-center justify-center rounded-md bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-900 text-white px-5 py-2.5 text-sm font-medium hover:opacity-90 disabled:opacity-50"
              >
                {resendBusy ? "Sending…" : "Send a fresh verification email"}
              </button>
              <div>
                <Link
                  to="/login"
                  className="text-sm text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100 underline"
                >
                  Back to sign in
                </Link>
              </div>
            </div>
          )}

          {status === "error" && (
            <div className="space-y-3">
              <button
                type="button"
                onClick={onResend}
                disabled={resendBusy}
                className="inline-flex items-center justify-center rounded-md border border-zinc-300 dark:border-zinc-700 px-4 py-2 text-sm font-medium hover:bg-zinc-50 dark:hover:bg-zinc-900 disabled:opacity-50"
              >
                {resendBusy ? "Sending…" : "Send a new verification email"}
              </button>
              <div>
                <Link
                  to="/login"
                  className="text-sm text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100 underline"
                >
                  Back to sign in
                </Link>
              </div>
            </div>
          )}

          {resendMessage && (
            <p className="text-xs text-emerald-700 dark:text-emerald-400 mt-4">
              {resendMessage}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function titleFor(status: VerifyStatus): string {
  switch (status) {
    case "loading":
      return "Verifying your email…";
    case "success":
      return "You're verified.";
    case "expired":
      return "This link has expired.";
    case "error":
    default:
      return "Verification didn't work.";
  }
}

function StatusIcon({ status }: { status: VerifyStatus }) {
  if (status === "loading") {
    return (
      <div className="inline-block w-12 h-12 border-2 border-zinc-300 dark:border-zinc-700 border-t-zinc-900 dark:border-t-zinc-100 rounded-full animate-spin" />
    );
  }
  if (status === "success") {
    return (
      <div className="inline-flex w-12 h-12 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 text-2xl">
        ✓
      </div>
    );
  }
  // error / expired
  return (
    <div className="inline-flex w-12 h-12 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 text-2xl">
      !
    </div>
  );
}
